import type { ReactNode } from 'react';

/**
 * A titled group of fields.
 *
 * design-system.md §8 — the prototype's careers form groups its fields
 * (`Applicant Information`, `Position Information`) and does it well. Long
 * forms without sections are the ones people abandon halfway.
 *
 * `<fieldset>` and `<legend>` rather than a div and a heading, because that is
 * what tells a screen reader these fields belong together.
 */
export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <fieldset className="border-t border-ink-300 pt-5 first:border-t-0 first:pt-0">
      <legend className="float-left w-full pb-1 text-h3 text-ink-900">{title}</legend>
      {description ? <p className="clear-left mb-4 text-body text-ink-500">{description}</p> : null}
      <div className="clear-left space-y-4">{children}</div>
    </fieldset>
  );
}

/**
 * The line that goes above a form with required fields.
 *
 * design-system.md §8 asks for it by name. An asterisk with nothing explaining
 * it is a convention people have to already know.
 */
export function RequiredFieldsNote() {
  return (
    <p className="text-meta text-ink-500">
      Fields marked with <span className="text-bark-700">*</span> are required.
    </p>
  );
}
